import React from "react";
import PropTypes from "prop-types";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import {Button, Alert} from "reactstrap";

const MAX_EDGE = 4096;
const DEBOUNCE_MS = 220;
const JPEG_QUALITY_MIN = 0.05;
const JPEG_QUALITY_MAX = 1;

function formatBytes(bytes) {
	if (bytes === null || bytes === undefined || Number.isNaN(bytes)) {
		return "—";
	}
	if (bytes < 1024) {
		return `${bytes} B`;
	}
	if (bytes < 1024 * 1024) {
		return `${(bytes / 1024).toFixed(1)} KB`;
	}
	return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * @param {File} file
 * @returns {{ mime: string, ext: string, usesQuality: boolean, opaqueFill: boolean, formatNote: ?string }}
 */
function getOutputProfile(file) {
	const type = (file.type || "").toLowerCase().trim();
	const name = (file.name || "").toLowerCase();

	const byMime = () => {
		if (type === "image/jpeg" || type === "image/pjpeg") {
			return "jpeg";
		}
		if (type === "image/png") {
			return "png";
		}
		if (type === "image/webp") {
			return "webp";
		}
		if (type === "image/gif") {
			return "gif";
		}
		return null;
	};

	const byName = () => {
		if (/\.jpe?g$/i.test(name)) {
			return "jpeg";
		}
		if (/\.png$/i.test(name)) {
			return "png";
		}
		if (/\.webp$/i.test(name)) {
			return "webp";
		}
		if (/\.gif$/i.test(name)) {
			return "gif";
		}
		return null;
	};

	const kind = byMime() || byName() || "jpeg";

	if (kind === "jpeg") {
		return {mime: "image/jpeg", ext: "jpg", usesQuality: true, opaqueFill: true, formatNote: null};
	}
	if (kind === "png") {
		return {mime: "image/png", ext: "png", usesQuality: false, opaqueFill: false, formatNote: null};
	}
	if (kind === "webp") {
		return {mime: "image/webp", ext: "webp", usesQuality: true, opaqueFill: false, formatNote: null};
	}
	// GIF (and similar): canvas cannot encode GIF; save raster as PNG.
	return {
		mime: "image/png",
		ext: "png",
		usesQuality: false,
		opaqueFill: false,
		formatNote: "Browsers cannot create GIF files from a canvas. The first frame is saved as PNG.",
	};
}

function outputFileName(originalName, ext) {
	const trimmed = (originalName || "image").trim() || "image";
	const dot = trimmed.lastIndexOf(".");
	const base = dot > 0 ? trimmed.slice(0, dot) : trimmed;
	return `${base}_compressed.${ext}`;
}

/**
 * Client-side re-encode with quality where the format supports it. No server upload.
 */
export default class ImageCompression extends React.Component {
	static propTypes = {
		locations: PropTypes.array.isRequired,
		states: PropTypes.array.isRequired,
		history: PropTypes.object.isRequired,
	};

	constructor(props) {
		super(props);
		this.fileInputRef = React.createRef();
		this.previewObjectUrl = null;
		this.encodeTimer = null;
		this.state = {
			file: null,
			previewUrl: null,
			originalSize: null,
			qualityPercent: 85,
			compressedSize: null,
			encoding: false,
			encodeError: null,
			lastBlob: null,
			outputProfile: null,
		};
	}

	componentWillUnmount() {
		this.clearEncodeTimer();
		this.revokePreviewUrl();
	}

	clearEncodeTimer = () => {
		if (this.encodeTimer) {
			clearTimeout(this.encodeTimer);
			this.encodeTimer = null;
		}
	};

	revokePreviewUrl = () => {
		if (this.previewObjectUrl) {
			URL.revokeObjectURL(this.previewObjectUrl);
			this.previewObjectUrl = null;
		}
	};

	qualityFromPercent = (percent) => {
		const t = Math.min(100, Math.max(1, percent)) / 100;
		return JPEG_QUALITY_MIN + t * (JPEG_QUALITY_MAX - JPEG_QUALITY_MIN);
	};

	scheduleEncode = () => {
		this.clearEncodeTimer();
		if (!this.state.file) {
			return;
		}
		if (this.state.outputProfile && !this.state.outputProfile.usesQuality) {
			return;
		}
		this.encodeTimer = setTimeout(() => {
			this.encodeTimer = null;
			this.runEncode();
		}, DEBOUNCE_MS);
	};

	runEncode = async () => {
		const {file, qualityPercent, outputProfile} = this.state;
		if (!file || !outputProfile) {
			return;
		}
		this.setState({encoding: true, encodeError: null});
		try {
			const quality = outputProfile.usesQuality ? this.qualityFromPercent(qualityPercent) : undefined;
			const blob = await this.encodeFileToBlob(file, outputProfile, quality);
			this.setState({
				compressedSize: blob.size,
				lastBlob: blob,
				encoding: false,
			});
		} catch (e) {
			this.setState({
				encoding: false,
				compressedSize: null,
				lastBlob: null,
				encodeError: e.message || "Could not compress this image. Try a JPEG or PNG file, or a smaller photo.",
			});
		}
	};

	encodeFileToBlob = (file, profile, quality) =>
		new Promise((resolve, reject) => {
			const img = new Image();
			const objectUrl = URL.createObjectURL(file);
			img.onload = () => {
				URL.revokeObjectURL(objectUrl);
				try {
					let {naturalWidth: w, naturalHeight: h} = img;
					if (!w || !h) {
						reject(new Error("Invalid image dimensions."));
						return;
					}
					if (w > MAX_EDGE || h > MAX_EDGE) {
						const scale = Math.min(MAX_EDGE / w, MAX_EDGE / h);
						w = Math.round(w * scale);
						h = Math.round(h * scale);
					}
					const canvas = document.createElement("canvas");
					canvas.width = w;
					canvas.height = h;
					const ctx = canvas.getContext("2d");
					if (profile.opaqueFill) {
						ctx.fillStyle = "#ffffff";
						ctx.fillRect(0, 0, w, h);
					}
					ctx.drawImage(img, 0, 0, w, h);

					const tryBlob = (mime, q) =>
						new Promise((res, rej) => {
							const done = (blob) => {
								if (!blob) {
									rej(new Error("encode_failed"));
									return;
								}
								res(blob);
							};
							if (q !== undefined && q !== null) {
								canvas.toBlob(done, mime, q);
							} else {
								canvas.toBlob(done, mime);
							}
						});

					const finish = async () => {
						if (profile.mime === "image/jpeg") {
							return tryBlob("image/jpeg", quality);
						}
						if (profile.mime === "image/png") {
							return tryBlob("image/png");
						}
						if (profile.mime === "image/webp") {
							return tryBlob("image/webp", quality);
						}
						return tryBlob("image/png");
					};

					finish().then(resolve).catch((err) => {
						if (err && err.message === "encode_failed") {
							const hint =
								profile.mime === "image/webp"
									? "This browser could not save WebP. Try Chrome or Edge, or use JPEG or PNG."
									: "Browser could not create an image in the selected format.";
							reject(new Error(hint));
							return;
						}
						reject(err);
					});
				} catch (err) {
					reject(err);
				}
			};
			img.onerror = () => {
				URL.revokeObjectURL(objectUrl);
				reject(new Error("Could not read image (unsupported format or corrupt file)."));
			};
			img.src = objectUrl;
		});

	handlePickClick = () => {
		if (this.fileInputRef.current) {
			this.fileInputRef.current.click();
		}
	};

	handleFileChange = (e) => {
		const file = e.target.files && e.target.files[0];
		e.target.value = "";
		if (!file) {
			return;
		}
		if (!file.type.startsWith("image/")) {
			this.setState({
				encodeError: "Please choose an image file.",
				file: null,
				previewUrl: null,
				originalSize: null,
				compressedSize: null,
				lastBlob: null,
				outputProfile: null,
			});
			return;
		}
		this.revokePreviewUrl();
		const previewUrl = URL.createObjectURL(file);
		this.previewObjectUrl = previewUrl;
		const outputProfile = getOutputProfile(file);
		this.setState(
			{
				file,
				previewUrl,
				originalSize: file.size,
				compressedSize: null,
				lastBlob: null,
				encodeError: null,
				outputProfile,
			},
			() => {
				this.runEncode();
			}
		);
	};

	handleQualityChange = (e) => {
		const qualityPercent = Number(e.target.value);
		this.setState({qualityPercent}, () => this.scheduleEncode());
	};

	handleDownload = () => {
		const {lastBlob, file, outputProfile} = this.state;
		if (!lastBlob || !file || !outputProfile) {
			return;
		}
		const url = URL.createObjectURL(lastBlob);
		const a = document.createElement("a");
		a.href = url;
		a.download = outputFileName(file.name, outputProfile.ext);
		document.body.appendChild(a);
		a.click();
		a.remove();
		setTimeout(() => URL.revokeObjectURL(url), 0);
	};

	render() {
		const {
			previewUrl,
			originalSize,
			qualityPercent,
			compressedSize,
			encoding,
			encodeError,
			lastBlob,
			file,
			outputProfile,
		} = this.state;

		const usesQuality = outputProfile && outputProfile.usesQuality;
		const formatLabel = outputProfile ? outputProfile.ext.toUpperCase() : "";

		return (
			<div className="row">
				<div className="col-md-10 col-lg-8">
					<Card>
						<CardContent>
							<p className="text-muted" style={{marginBottom: "1rem"}}>
								Images are re-encoded in your browser. Output format matches the file you pick (JPEG,
								PNG, or WebP). Some formats (for example HEIC) may not work in every browser.
							</p>
							<input
								ref={this.fileInputRef}
								type="file"
								accept="image/*"
								style={{display: "none"}}
								onChange={this.handleFileChange}
							/>
							<Button color="primary" onClick={this.handlePickClick} className="mb-3">
								Choose photo
							</Button>
							{encodeError && (
								<Alert color="warning" className="mb-3">
									{encodeError}
								</Alert>
							)}
							{outputProfile && outputProfile.formatNote && (
								<Alert color="info" className="mb-3">
									{outputProfile.formatNote}
								</Alert>
							)}
							{previewUrl && (
								<div className="mb-3">
									<img
										src={previewUrl}
										alt="Selected"
										style={{maxWidth: "100%", maxHeight: "420px", objectFit: "contain"}}
									/>
								</div>
							)}
							{file && outputProfile && (
								<div className="mb-3">
									<p>
										<strong>Original size:</strong> {formatBytes(originalSize)}
									</p>
									{usesQuality ? (
										<>
											<label htmlFor="image-compression-quality" className="d-block mb-1">
												<strong>Compression (quality):</strong> {qualityPercent}%
											</label>
											<input
												id="image-compression-quality"
												type="range"
												min={1}
												max={100}
												value={qualityPercent}
												onChange={this.handleQualityChange}
												className="w-100"
												style={{maxWidth: "400px"}}
											/>
										</>
									) : (
										<p className="text-muted mb-0">
											<strong>Lossless output:</strong>{" "}
											{outputProfile.formatNote
												? "Quality is not adjustable; only very large images are scaled down."
												: "PNG is lossless in the browser (no quality slider). Only very large images are scaled down."}
										</p>
									)}
									<p className="mt-2 mb-0">
										<strong>Estimated compressed size:</strong>{" "}
										{encoding ? "Calculating…" : formatBytes(compressedSize)}
									</p>
									{usesQuality &&
										compressedSize != null &&
										originalSize != null &&
										compressedSize > originalSize &&
										!encoding && (
										<p className="text-muted small mt-2 mb-0">
											This is normal: the file is decoded to pixels, then saved again with this
											browser’s encoder. High quality is not the same as “original bytes,” so the
											new file can be larger than a heavily compressed original.
										</p>
									)}
								</div>
							)}
							{file && outputProfile && (
								<Button color="success" disabled={!lastBlob || encoding || !!encodeError} onClick={this.handleDownload}>
									Download compressed {formatLabel}
								</Button>
							)}
						</CardContent>
					</Card>
				</div>
			</div>
		);
	}
}
